import axios, { AxiosResponse } from 'axios'
import StackTrace from 'stacktrace-js'

import {
  ErrorOptions,
  InfoOptions,
  LogDTO,
  LoggerOptions,
  LogLevel,
  SanitizedAxiosResponse,
  WarningOptions,
} from './types'

export default class Logger {
  private ERROR: LogLevel = {
    level: 1,
    name: 'ERROR',
    color: 'red',
  }

  private WARN: LogLevel = {
    level: 2,
    name: 'WARNING',
    color: 'orange',
  }

  private INFO: LogLevel = {
    level: 3,
    name: 'INFO',
    color: 'lightblue',
  }

  private url: string

  private headers: Object

  private level: number

  private console = window.console

  private appName: string

  constructor(options: LoggerOptions) {
    this.appName = options.appName
    this.url = options.url
    this.level = options.level || this.ERROR.level
    this.headers = options.headers || { 'Content-Type': 'application/json' }
  }

  async error(message: string, metadata: ErrorOptions) {
    await this.log(this.ERROR, message, metadata)
  }

  async warn(message: string, metadata: WarningOptions) {
    await this.log(this.WARN, message, metadata)
  }

  async info(message: string, metadata?: InfoOptions) {
    await this.log(this.INFO, message, metadata)
  }

  private sanitizeAxiosResponse(
    response: AxiosResponse,
  ): SanitizedAxiosResponse {
    const {
      status,
      statusText,
      data,
      config: { url, method },
    } = response

    return {
      status,
      statusText,
      data,
      config: {
        url,
        method,
      },
    }
  }

  private async getErrorDetails(
    metadata?: WarningOptions | ErrorOptions,
  ): Promise<{
    message: string
    stackTrace: StackTrace.StackFrame[]
    response?: SanitizedAxiosResponse
  }> {
    let stackTrace = []
    try {
      stackTrace = await StackTrace.fromError(metadata.error)
    } catch (e) {
      // skip sending stack trace if unable to retrieve it
    }

    const { message } = metadata.error
    const axiosResponse =
      'response' in metadata.error && metadata.error.response

    if (!axiosResponse) {
      return {
        message,
        stackTrace,
      }
    }

    const response = this.sanitizeAxiosResponse(axiosResponse)

    return {
      message,
      stackTrace,
      response,
    }
  }

  private async getLogDTO(
    logLevel: LogLevel,
    message: string,
    metadata?: InfoOptions | WarningOptions | ErrorOptions,
  ): Promise<LogDTO> {
    const logDTO: LogDTO = {
      app_name: this.appName,
      level_name: logLevel.name,
      message,
      context: {
        userAgent: navigator.userAgent,
        windowSize: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      },
    }

    if (!metadata) {
      return logDTO
    }

    const { tags, additionalData } = metadata
    const error = 'error' in metadata && (await this.getErrorDetails(metadata))

    if (!error) {
      return {
        ...logDTO,
        metadata: {
          tags,
          additionalData: JSON.stringify(additionalData),
        },
      }
    }

    return {
      ...logDTO,
      metadata: {
        tags,
        additionalData: JSON.stringify(additionalData),
        error: {
          message: error.message,
          stackTrace: JSON.stringify(error.stackTrace),
          response: JSON.stringify(error.response),
        },
      },
    }
  }

  private async send(
    logLevel: LogLevel,
    message: string,
    metadata?: InfoOptions | WarningOptions | ErrorOptions,
  ) {
    const logDTO = await this.getLogDTO(logLevel, message, metadata)

    this.console.log(
      `%c${logLevel.name}`,
      `color: ${logLevel.color}`,
      message,
      logDTO || '',
    )

    await axios({
      method: 'POST',
      url: this.url,
      data: logDTO,
      headers: this.headers,
    })
  }

  private async log(
    errorLevel: LogLevel,
    message: string,
    metadata?: InfoOptions | WarningOptions | ErrorOptions,
  ) {
    if (errorLevel.level <= this.level) {
      await this.send(errorLevel, message, metadata)
    }
  }
}
